import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '../client';

export type RolApp = 'admin_aosenuma' | 'tecnico_aosenuma' | 'cliente_elawan' | 'auditor';

export interface AosenumaSession {
  /** auth.users record completo. */
  user: User | null;
  /** Rol del usuario inyectado por custom_access_token_hook. */
  rolApp: RolApp | null;
  /** tenant_slug ('elawan' por default). null si user no en public.usuarios. */
  tenantSlug: string | null;
  /** true si el JWT actual viene de un factor MFA reciente (aal2). */
  isAal2: boolean;
  /**
   * Epoch ms del último `amr` con method=totp. null si nunca challenge TOTP.
   * Sirve para enforce TTL de re-MFA en UI (consumidor compara con Date.now()).
   * El JWT se renueva cada hora pero Supabase NO re-emite amr.totp en cada
   * refresh — solo cuando hay un challenge nuevo. Por eso este timestamp es
   * estable hasta que el user vuelva a verificar TOTP.
   */
  mfaTimestamp: number | null;
  /** Cargando estado inicial. */
  loading: boolean;
}

/**
 * Decode JWT payload (sin verificar firma — solo lectura para claims).
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === 'function'
      ? decodeURIComponent(escape(atob(padded)))
      : Buffer.from(padded, 'base64').toString('utf8');
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Estado global compartido + UNA sola suscripción a onAuthStateChange.
 *
 * Crítico: cada mount de `useSession` antes creaba una suscripción nueva
 * a Supabase Auth, y múltiples subscribers competían por el Web Lock
 * interno de @supabase/auth-js → 'Lock was stolen by another request' →
 * onAuthStateChange dejaba de disparar → loop infinito de safety timeout.
 *
 * Ahora: una suscripción única a nivel módulo, los componentes solo
 * se enganchan al store local via listeners.
 */
type Snapshot = { session: Session | null; isAal2: boolean; loading: boolean };

const SNAPSHOT_KEY = '__aosenuma_session_snapshot__';
const LISTENERS_KEY = '__aosenuma_session_listeners__';
const INIT_KEY = '__aosenuma_session_init__';

type GlobalStore = typeof globalThis & {
  [SNAPSHOT_KEY]?: Snapshot;
  [LISTENERS_KEY]?: Set<(s: Snapshot) => void>;
  [INIT_KEY]?: boolean;
};

function getStore(): GlobalStore {
  return globalThis as GlobalStore;
}

function getSnapshot(): Snapshot {
  const g = getStore();
  if (!g[SNAPSHOT_KEY]) {
    g[SNAPSHOT_KEY] = { session: null, isAal2: false, loading: isSupabaseConfigured() };
  }
  return g[SNAPSHOT_KEY]!;
}

function getListeners(): Set<(s: Snapshot) => void> {
  const g = getStore();
  if (!g[LISTENERS_KEY]) g[LISTENERS_KEY] = new Set();
  return g[LISTENERS_KEY]!;
}

function setSnapshot(next: Partial<Snapshot>) {
  const g = getStore();
  g[SNAPSHOT_KEY] = { ...getSnapshot(), ...next };
  for (const l of getListeners()) l(g[SNAPSHOT_KEY]!);
}

/**
 * Inicializa UNA sola vez la suscripción global a onAuthStateChange y
 * carga la sesión inicial desde localStorage. Idempotente.
 */
function ensureInitialized(): void {
  const g = getStore();
  if (g[INIT_KEY]) return;
  if (!isSupabaseConfigured()) {
    setSnapshot({ loading: false });
    g[INIT_KEY] = true;
    return;
  }
  g[INIT_KEY] = true;
  const sb = getSupabase();

  // 1) Lectura inmediata desde localStorage (sin pasar por auth-js que
  //    puede tener locks corruptos en dev).
  try {
    const ref = (sb as unknown as { auth: { storageKey?: string } }).auth.storageKey
      ?? 'sb-tpvsetuadwuijczlqbtl-auth-token';
    const raw = window.localStorage.getItem(ref);
    if (raw) {
      const parsed = JSON.parse(raw) as { currentSession?: Session } | Session | null;
      const recovered = (parsed && 'currentSession' in (parsed as Record<string, unknown>)
        ? (parsed as { currentSession?: Session }).currentSession
        : (parsed as Session | null)) ?? null;
      if (recovered?.access_token) {
        setSnapshot({ session: recovered, loading: false });
      } else {
        setSnapshot({ loading: false });
      }
    } else {
      setSnapshot({ loading: false });
    }
  } catch {
    setSnapshot({ loading: false });
  }

  // 2) Suscripción única a cambios futuros (login, logout, refresh).
  //    NO bloquea el render — corre en background. Si auth-js cuelga,
  //    el snapshot inicial ya quedó cargado del paso #1.
  try {
    sb.auth.onAuthStateChange((event, s) => {
      // En SIGNED_OUT y SIGNED_IN, limpiar el cache de useCachedFetch.
      // Sin esto, si el user A cierra sesión y entra el user B en la
      // misma pestaña, B ve el sessionStorage de A (que puede tener PII
      // masked de otro tenant + KPIs ajenos) hasta que expire el TTL o
      // llegue un evento realtime. Empty cache = empty render en el
      // primer fetch limpio.
      if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
        try {
          if (typeof window !== 'undefined') {
            const keys = Object.keys(window.sessionStorage)
              .filter((k) => k.startsWith('aosenuma-cache:'));
            for (const k of keys) window.sessionStorage.removeItem(k);
          }
        } catch { /* QuotaExceeded o storage corrupto */ }
      }
      // Audit log → BFF: SIGNED_IN y MFA_CHALLENGE_VERIFIED van a
      // eventos_negociacion para que aparezcan en BitácoraPage. Sin esto
      // la cadena LFPDPPP es incompleta (no se sabe quién y cuándo entró).
      // Idempotente por `iat` del JWT — recargar la página dispara
      // SIGNED_IN otra vez con el mismo token y NO se reproyecta.
      if (s?.access_token && (event === 'SIGNED_IN' || event === 'MFA_CHALLENGE_VERIFIED')) {
        void emitAuthAuditEvent(event, s.access_token);
      }
      setSnapshot({ session: s, loading: false });
      // AAL en background sin bloquear.
      if (s) {
        void Promise.race([
          sb.auth.mfa.getAuthenticatorAssuranceLevel(),
          new Promise<{ data?: { currentLevel?: string } }>((res) =>
            window.setTimeout(() => res({ data: undefined }), 1500),
          ),
        ]).then((r) => {
          setSnapshot({ isAal2: r.data?.currentLevel === 'aal2' });
        });
      } else {
        setSnapshot({ isAal2: false });
      }
    });
  } catch {
    // Si la suscripción falla, el snapshot inicial ya está cargado.
  }
}

export function useSession(): AosenumaSession {
  ensureInitialized();
  const [snap, setSnap] = useState<Snapshot>(() => getSnapshot());

  useEffect(() => {
    const listener = (s: Snapshot) => setSnap(s);
    const listeners = getListeners();
    listeners.add(listener);
    // Sync inicial por si hubo update entre useState y useEffect.
    setSnap(getSnapshot());
    return () => { listeners.delete(listener); };
  }, []);

  let rolApp: RolApp | null = null;
  let tenantSlug: string | null = null;
  let mfaTimestamp: number | null = null;
  if (snap.session?.access_token) {
    const payload = decodeJwtPayload(snap.session.access_token);
    const meta = payload?.app_metadata as Record<string, unknown> | undefined;
    if (typeof meta?.rol_app === 'string') rolApp = meta.rol_app as RolApp;
    if (typeof meta?.tenant_slug === 'string') tenantSlug = meta.tenant_slug;
    const amr = payload?.amr;
    if (Array.isArray(amr)) {
      const totpEntries = amr.filter(
        (e): e is { method: string; timestamp: number } =>
          typeof e === 'object' && e != null
          && (e as { method?: unknown }).method === 'totp'
          && typeof (e as { timestamp?: unknown }).timestamp === 'number',
      );
      if (totpEntries.length > 0) {
        mfaTimestamp = Math.max(...totpEntries.map((e) => e.timestamp)) * 1000;
      }
    }
  }

  return {
    user: snap.session?.user ?? null,
    rolApp,
    tenantSlug,
    isAal2: snap.isAal2,
    mfaTimestamp,
    loading: snap.loading,
  };
}

/* ============================== Audit log → BFF ============================== */

/**
 * Llave en localStorage para deduplicar emisiones de auth events. Guardamos
 * `${event}:${iat}` del último JWT proyectado al BFF. Si el mismo iat
 * vuelve (page reload, doble dispatch del SDK), no reemitimos.
 *
 * El BFF también deduplica server-side dentro de 5 min como defensa adicional,
 * pero el filtro client-side ahorra una request HTTP cada reload.
 */
const AUDIT_LAST_KEY = 'aosenuma-audit-last-auth';

function readLastAudit(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(AUDIT_LAST_KEY);
  } catch {
    return null;
  }
}

function writeLastAudit(value: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(AUDIT_LAST_KEY, value);
  } catch { /* QuotaExceeded — ignorar, dedup degrada a server-side */ }
}

function resolveApiBase(): string {
  const env = (
    typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env as unknown as Record<string, string | undefined>)
      : {}
  );
  const raw = env.VITE_PUBLIC_API_BASE_URL?.trim();
  if (raw && raw.length > 0) return raw.replace(/\/$/, '');
  return 'http://localhost:8787';
}

/**
 * Emite SIGNED_IN / MFA_CHALLENGE_VERIFIED al BFF para que aparezca en
 * BitácoraPage. Idempotente (dedup por `iat` en localStorage). Fire-and-forget:
 * fallos de red NO afectan el flujo de auth — el log es best-effort.
 */
async function emitAuthAuditEvent(event: 'SIGNED_IN' | 'MFA_CHALLENGE_VERIFIED', token: string): Promise<void> {
  try {
    const payload = decodeJwtPayload(token);
    const iat = typeof payload?.iat === 'number' ? payload.iat : null;
    if (iat == null) return;

    const dedupKey = `${event}:${iat}`;
    if (readLastAudit() === dedupKey) return;

    const provider = payload && typeof (payload as { app_metadata?: { provider?: unknown } }).app_metadata === 'object'
      ? (payload as { app_metadata?: { provider?: string } }).app_metadata?.provider
      : undefined;

    const url = `${resolveApiBase()}/v1/audit/auth-event`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ event, provider }),
      // Keepalive para que la request sobreviva a una navegación que pueda
      // suceder justo después del SIGNED_IN (redirect post-login).
      keepalive: true,
    });
    if (res.ok) {
      writeLastAudit(dedupKey);
    }
  } catch {
    // Best-effort: si el BFF no responde, no rompemos el login. El próximo
    // intento (otro login o MFA verify) reintentará. Sentry FE captura
    // unhandled, pero esto sí está handled.
  }
}
